import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { routeForForm, type PrescriptionRoute } from '@his/shared';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Input, SuggestionPopover } from '@/components/ui/primitives';

export interface DrugChoice {
  productId: string | null;
  drugName: string;
  strength?: string;
  form?: string;
  route?: PrescriptionRoute | '';
  frequency?: string;
  dose?: string;
  isControlled?: boolean;
}

interface FormularyRow {
  id: string;
  genericName: string;
  brandName: string | null;
  strength: string | null;
  form: string | null;
  isControlled: boolean;
}

interface RecentRow {
  drugName: string;
  strength: string | null;
  form: string | null;
  route: string;
  frequency: string;
  dose: string;
  productId: string | null;
}

export function DrugSearchInput({
  value,
  readOnly,
  onType,
  onChoose,
}: {
  value: string;
  readOnly?: boolean;
  onType: (text: string) => void;
  onChoose: (choice: DrugChoice) => void;
}) {
  const { hasModule } = useAuth();
  const formulary = hasModule('pharmacy');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setTerm(value.trim()), 500);
    return () => clearTimeout(timer);
  }, [value]);

  const suggestions = useQuery({
    queryKey: ['drug-search', formulary, term],
    queryFn: async (): Promise<DrugChoice[]> => {
      if (formulary) {
        const rows = await get<FormularyRow[]>(`/formulary/search${qs({ q: term })}`);
        return rows.map((row) => ({
          productId: row.id,
          drugName: row.brandName ? `${row.brandName} (${row.genericName})` : row.genericName,
          strength: row.strength ?? undefined,
          form: row.form ?? undefined,
          route: routeForForm(row.form) ?? '',
          isControlled: row.isControlled,
        }));
      }
      const rows = await get<RecentRow[]>(`/prescriptions/recent-drugs${qs({ q: term })}`);
      return rows.map((row) => ({
        productId: row.productId,
        drugName: row.drugName,
        strength: row.strength ?? undefined,
        form: row.form ?? undefined,
        route: row.route as PrescriptionRoute,
        frequency: row.frequency,
        dose: row.dose,
      }));
    },
    enabled: !readOnly && open && term.length >= 2,
    staleTime: 60_000,
  });

  return (
    <SuggestionPopover
      open={open && (suggestions.data?.length ?? 0) > 0}
      onDismiss={() => setOpen(false)}
      anchor={
        <Input
          value={value}
          readOnly={readOnly}
          placeholder={formulary ? tr('opd', 'search_the_formulary') : tr('opd', 'paracetamol')}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            onType(e.target.value);
            setOpen(true);
          }}
        />
      }
    >
      <ul className="max-h-60 overflow-y-auto p-1 text-sm" role="listbox">
        {suggestions.data?.map((choice, index) => (
          <li key={`${choice.productId ?? choice.drugName}-${index}`}>
            <button
              type="button"
              className="w-full rounded px-2 py-1 text-start transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChoose(choice);
                setOpen(false);
              }}
            >
              <span className="font-medium">{choice.drugName}</span>
              {choice.strength ? <span className="text-muted-foreground"> {choice.strength}</span> : null}
              {choice.form ? <span className="text-muted-foreground"> · {choice.form}</span> : null}
            </button>
          </li>
        ))}
      </ul>
    </SuggestionPopover>
  );
}
